import { useState, type FormEvent } from 'react';
import type { Agent } from '@localagent/sdk';
import { Button } from '@localagent/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@localagent/ui/components/dialog';
import { Input } from '@localagent/ui/components/input';
import { Label } from '@localagent/ui/components/label';
import { useAgentDefinitions } from '../../hooks/useAgentDefinitions';
import { useCreateAgent, useDeleteAgent, useUpdateAgent } from '../../hooks/useAgents';

const FIELD_CLASS =
  'w-full rounded-md bg-muted px-3 py-2 text-body focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50';

export function CreateAgentDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (agent: Agent) => void;
}) {
  const personas = useAgentDefinitions();
  const createAgent = useCreateAgent();
  const [personaId, setPersonaId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const reset = () => {
    setPersonaId('');
    setName('');
    setDescription('');
    createAgent.reset();
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!personaId || !trimmed) return;
    const trimmedDescription = description.trim();
    createAgent.mutate(
      {
        persona_id: personaId,
        name: trimmed,
        ...(trimmedDescription ? { description: trimmedDescription } : {}),
      },
      {
        onSuccess: (agent) => {
          reset();
          onCreated(agent);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) close(); }}>
      <DialogContent className="max-w-lg" data-testid="agent-create-dialog">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>New Agent</DialogTitle>
            <DialogDescription>
              An Agent runs its persona&apos;s current configuration. To change how it behaves, edit the persona.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor="agent-create-persona" required>Persona</Label>
            <select
              id="agent-create-persona"
              data-testid="agent-create-persona"
              value={personaId}
              onChange={(e) => setPersonaId(e.target.value)}
              disabled={personas.isLoading}
              className={FIELD_CLASS}
            >
              <option value="">{personas.isLoading ? 'Loading personas…' : 'Choose a persona…'}</option>
              {(personas.data ?? []).map((persona) => (
                <option key={persona.id} value={persona.id}>{persona.name}</option>
              ))}
            </select>
            {personas.error && (
              <p className="text-body text-destructive">Failed to load personas: {personas.error.message}</p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="agent-create-name" required>Name</Label>
            <Input
              id="agent-create-name"
              data-testid="agent-create-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Release Reviewer"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="agent-create-description">Description</Label>
            <textarea
              id="agent-create-description"
              data-testid="agent-create-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className={FIELD_CLASS}
            />
          </div>
          {createAgent.error && (
            <p data-testid="agent-create-error" className="text-body text-destructive">
              {createAgent.error.message}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>Cancel</Button>
            <Button
              type="submit"
              data-testid="agent-create-submit"
              disabled={!personaId || !name.trim() || createAgent.isPending}
            >
              {createAgent.isPending ? 'Creating…' : 'Create Agent'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function RenameAgentDialog({
  agent,
  open,
  onClose,
}: {
  agent: Agent;
  open: boolean;
  onClose: () => void;
}) {
  const updateAgent = useUpdateAgent();
  const [name, setName] = useState(agent.name);

  const close = () => {
    updateAgent.reset();
    onClose();
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || trimmed === agent.name) return;
    updateAgent.mutate({ agentId: agent.id, name: trimmed }, { onSuccess: close });
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) close(); }}>
      <DialogContent className="max-w-md" data-testid="agent-rename-dialog">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Rename Agent</DialogTitle>
            <DialogDescription>Agent names are unique within the account.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor="agent-rename-name" required>Name</Label>
            <Input
              id="agent-rename-name"
              data-testid="agent-rename-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          {updateAgent.error && (
            <p data-testid="agent-rename-error" className="text-body text-destructive">
              {updateAgent.error.message}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>Cancel</Button>
            <Button
              type="submit"
              data-testid="agent-rename-submit"
              disabled={!name.trim() || name.trim() === agent.name || updateAgent.isPending}
            >
              {updateAgent.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteAgentDialog({
  agent,
  open,
  onClose,
  onDeleted,
}: {
  agent: Agent;
  open: boolean;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const deleteAgent = useDeleteAgent();

  const close = () => {
    deleteAgent.reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) close(); }}>
      <DialogContent className="max-w-md" data-testid="agent-delete-dialog">
        <DialogHeader>
          <DialogTitle>Delete Agent</DialogTitle>
          <DialogDescription>
            Delete &quot;{agent.name}&quot;? It stops its active work and can no longer join chats or work.
            Its sessions and history stay.
          </DialogDescription>
        </DialogHeader>
        {deleteAgent.error && (
          <p data-testid="agent-delete-error" className="text-body text-destructive">
            {deleteAgent.error.message}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={close}>Cancel</Button>
          <Button
            variant="destructive"
            data-testid="agent-delete-confirm"
            disabled={deleteAgent.isPending}
            onClick={() => {
              deleteAgent.mutate({ agentId: agent.id }, {
                onSuccess: () => {
                  deleteAgent.reset();
                  onDeleted();
                },
              });
            }}
          >
            {deleteAgent.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
